import { NextPage } from 'next';
import React, { FC } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import JsonLd from '../../../components/layouts/JsonLd';
import MetaTags from '../../../components/layouts/MetaTags';
import MerchantBookingServicesPage from '../../../templates/merchants/booking-pages/IndexTemplate/MerchantBookingServicesPage';
import BookingPage, { Pagination } from '../../../domain/entities/BookingPage';
import {
  allCategoriesCache,
  getCategory,
} from '../../../infrastructure/api/backend/explore_api';
import { BookingPageRepositoryForSSR } from '../../../infrastructure/repositories/BookingPageRepository';
import { CategoryEntity } from '../../../interfaces/explore/CategoryEntity';
import { MerchantDetail } from '../../../interfaces/merchants';

import { fetchMerchantDetail } from '../../../infrastructure/api';
import { injectAppContextGetServerSideProps } from '../../_app';
import ErrorTemplate from '../../../templates/ErrorTemplate';
import { MerchantSite } from '../../../components/MerchantSite';
import { usePushMerchantDataLayerEffect } from '../../../hooks/gtm';
import { useRwgConversionTrackingToken } from 'src/hooks/rwg';
import { buildSiteUrl } from 'src/utils/request-url';

const titleBy = (intl: IntlShape, displayName: string, currentPage: number) => {
  const base = intl.formatMessage(
    { id: 'merchants.bookingPages.index.meta.title' },
    { displayName },
  );
  if (currentPage <= 1) {
    return base;
  }
  return `${base} ${intl.formatMessage(
    { id: 'merchants.bookingPages.index.page' },
    { currentPage },
  )}`;
};

interface Props {
  merchantDetail?: MerchantDetail;
  public_id: string;
  category?: CategoryEntity;
  categories: CategoryEntity[];
  bookingPages?: BookingPage[];
  pagination?: Pagination;
  isUnpublishedSite: boolean;
  isCoubicApp: boolean;
}

const BookingPages: FC<Props> = ({
  public_id,
  merchantDetail,
  category,
  bookingPages,
  pagination,
  isCoubicApp,
}) => {
  const intl = useIntl();
  const currentPage = pagination?.currentPage;

  usePushMerchantDataLayerEffect({ visit_merchant_id: merchantDetail?.id });
  useRwgConversionTrackingToken();

  if (merchantDetail && category && pagination && currentPage && bookingPages) {
    const title = titleBy(intl, merchantDetail.display_name, currentPage);

    return (
      <>
        <MetaTags
          title={title}
          description={
            merchantDetail.seo_setting?.description ||
            merchantDetail.description
          }
          keywords={merchantDetail.seo_setting?.keyword || ''}
          thumbnailUrl={merchantDetail.cover_image.url || ''}
          currentPath={buildSiteUrl(`/booking_pages?page=${currentPage}`)}
          noIndex={merchantDetail.seo_setting?.no_index}
          googleAnalyticsFourTrackingId={
            merchantDetail.available_google_analytics_four_tracking_id
          }
          faviconImageUrl={merchantDetail?.favicon_image?.url}
        />
        <JsonLd merchant={merchantDetail} />
        <MerchantBookingServicesPage
          merchantDetail={merchantDetail}
          public_id={public_id}
          category={category}
          bookingPages={bookingPages}
          pagination={pagination}
          isCoubicApp={isCoubicApp}
        />
      </>
    );
  }
  return <ErrorTemplate statusCode={404} />;
};

const MerchantBookingPages: NextPage<Props> = (props: Props) => {
  const { merchantDetail, isUnpublishedSite } = props;

  return (
    <MerchantSite
      isUnPublished={isUnpublishedSite}
      isMerchantInfoExist={!!merchantDetail}
      logoImage={merchantDetail?.logo_image || null}
      merchantPublicId={merchantDetail?.public_id || null}
      displayName={merchantDetail?.display_name || null}
      isCoubicApp={props.isCoubicApp}
    >
      <BookingPages {...props} />
    </MerchantSite>
  );
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (context) => {
    const { query, session, token, requestId, isCoubicApp } = context;
    const merchantPublicId = String(query.publicId);

    // page for pagination
    const page = query.page ? String(query.page) : '1';
    const repository = BookingPageRepositoryForSSR(session, token, requestId);

    const merchantDetailResponse = await fetchMerchantDetail({
      session,
      token,
      requestId,
      merchantPublicId,
    });
    if (merchantDetailResponse.redirect) {
      return { redirect: merchantDetailResponse.redirect };
    }
    const { merchant, isUnpublishedSite } = merchantDetailResponse;

    if (isUnpublishedSite) {
      return {
        props: {
          categories: [],
          merchantDetail: merchant,
          public_id: merchantPublicId,
          isUnpublishedSite,
          isCoubicApp,
        },
      };
    }

    const [categories, bookingPageList] = await Promise.all([
      // Layoutで取得している部分を徐々に置き換え
      allCategoriesCache(),
      // FIXME: BookingPageの定義を使っている最後の箇所なのでそのうち処す
      repository.Search(merchantPublicId, page),
    ]);

    // 予約ページが1件だけの場合はその予約ページ詳細にリダイレクトする
    if (bookingPageList?.pagination.totalSize === 1) {
      return {
        redirect: {
          destination: buildSiteUrl(
            `/${bookingPageList.bookingPages[0].publicId}#pageContent`,
            'next',
          ),
          permanent: false,
        },
      };
    }

    const category = merchant
      ? await getCategory(merchant.category_id)
      : undefined;
    const props: Props = {
      categories,
      category,
      merchantDetail: merchant,
      public_id: merchantPublicId,
      bookingPages: bookingPageList?.bookingPages,
      pagination: bookingPageList?.pagination,
      isUnpublishedSite,
      isCoubicApp,
    };

    return { props };
  },
);

export default MerchantBookingPages;
